import { Loader2, RefreshCw, SearchX } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslate } from 'ra-core';
import { CenteredSpinner } from '../../components/custom/CenteredSpinner';
import { Error as ErrorPage } from '../../components/admin/error';
import { Button } from '../../components/ui/button';
import { type UserDto } from '../../hooks/useGetUsers';
import {
  type FetchNextPageOptions,
  type InfiniteData,
  type InfiniteQueryObserverResult,
  type QueryObserverResult,
  type RefetchOptions,
} from '@tanstack/react-query';

// This component is fairly generic + could be used for things that aren't users, but we have no need for such a component yet, so we've left it specific for now

export type User = UserDto & { userId: string };

type UserInfiniteListProps = {
  users: User[];
  isLoadingUsers: boolean;
  isPendingUsers: boolean;
  isErrorUsers: boolean;
  errorUsers: globalThis.Error | null;
  isFetchNextPageError: boolean;
  isRefetching: boolean;
  renderUser: (user: User, index: number) => React.JSX.Element;
  hasNextPageUsers: boolean;
  refetchUsers: (options?: RefetchOptions) => Promise<
    QueryObserverResult<
      InfiniteData<{
        data: UserDto[];
        next: string | undefined;
      }>
    >
  >;
  fetchNextPageUsers: (options?: FetchNextPageOptions) => Promise<
    InfiniteQueryObserverResult<
      InfiniteData<{
        data: UserDto[];
        next: string | undefined;
      }>
    >
  >;
};

/**
 * Infinite-scrolling list of users that handles:
 * - initial loading / pending state
 * - empty state
 * - initial and subsequent fetch errors
 * - fetching the next page when scrolled near the end
 */
export const UserInfiniteList = ({
  renderUser,
  isErrorUsers,
  errorUsers,
  isFetchNextPageError,
  refetchUsers,
  isPendingUsers,
  users,
  isLoadingUsers,
  hasNextPageUsers,
  isRefetching,
  fetchNextPageUsers,
}: UserInfiniteListProps) => {
  const translate = useTranslate();
  const loadingIndicatorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Load more when the loading indicator comes into view
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          void fetchNextPageUsers({ cancelRefetch: false });
        }
      },
      {
        // If 10% of the loading indicator is visible
        threshold: 0.1,
      }
    );

    if (loadingIndicatorRef.current) {
      observer.observe(loadingIndicatorRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [
    fetchNextPageUsers,
    users.length, // This allows to re-check if we need to fetch more (such as loading an extra page if the container isn't full after the initial fetch)
  ]);

  if (isPendingUsers) {
    return <CenteredSpinner />;
  } else if (isErrorUsers) {
    return (
      <ErrorPage
        resetErrorBoundary={() => void refetchUsers()}
        error={errorUsers}
      />
    );
  }

  if (users.length === 0) {
    return (
      <div className="flex h-[55vh] flex-col items-center justify-center">
        <SearchX className="size-24 text-muted-foreground" />
        <p className="p-3 text-center text-muted-foreground">
          {translate('chat.createGroupsModalNoUsersFound')}
        </p>
      </div>
    );
  }

  return (
    <div className="h-[55vh] overflow-y-auto">
      {users.map((user, idx) => (
        <div key={user.id}>{renderUser(user, idx)}</div>
      ))}
      {isFetchNextPageError ? (
        <div className="flex h-full flex-col items-center justify-center gap-2 p-4">
          <p className="text-sm text-destructive">
            {translate('chat.errorLoadingMoreUsers')}
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void refetchUsers()}
            disabled={isLoadingUsers || isRefetching}
          >
            {isLoadingUsers || isRefetching ? (
              <Loader2 className="animate-spin" />
            ) : (
              <RefreshCw />
            )}
            {translate('chat.retry')}
          </Button>
        </div>
      ) : hasNextPageUsers ? (
        <div className="flex justify-center py-2" ref={loadingIndicatorRef}>
          <Loader2 className="size-6 animate-spin text-primary" />
        </div>
      ) : null}
    </div>
  );
};
